import React, { useEffect, useRef, useState } from "react";
import { PulseLoader } from "react-spinners";
import { AiChatRoles } from "../../constants/commonConstants";
import MarkdownRenderer from "../markdownRenderer";
import { IBuduGPTChatProps } from "../../models/common/IChatBot";

const ALLOWED_FILE_EXTENSIONS = ".pdf,.docx,.txt,.csv,.xlsx,.xls,.xml";
const ALLOWED_FILE_TYPES_LABEL = ALLOWED_FILE_EXTENSIONS.split(",")
  .map((ext) => ext.replace(".", "").toUpperCase())
  .join(", ");
const MAX_FILE_SIZE_MB = 10;
const validateChatFile = (file: File): boolean => {
  const ext = file.name.includes(".")
    ? `.${file.name.split(".").pop()?.toLowerCase()}`
    : "";
  if (!ext || !ALLOWED_FILE_EXTENSIONS.split(",").includes(ext)) {
    alert(`"${file.name}" is not a supported file type. Supported: ${ALLOWED_FILE_TYPES_LABEL}.`);
    return false;
  }
  if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
    alert(`File size must be under ${MAX_FILE_SIZE_MB} MB.`);
    return false;
  }
  return true;
};

const isFileDrag = (e: React.DragEvent<HTMLElement>): boolean =>
  Array.from(e.dataTransfer.types).includes("Files");

const WideToggle = ({ on }: { on: boolean }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 28 18"
    width={36}
    height={23}
    aria-hidden="true"
    style={{ display: "block" }}
  >
    <rect x={1} y={2} width={26} height={14} rx={7} fill={on ? "#948256" : "#bbb"} />
    <circle cx={on ? 20 : 8} cy={9} r={5} fill="#fff" />
  </svg>
);

const BuduGPTChat = ({
  isModalOpen,
  isFullScreen,
  chatHistory,
  loader,
  isBusy,
  isStreaming,
  chatMessage,
  feedbackGiven,
  uploadedFile,
  isUploadingFile,
  uploadError,
  searchGlobally,
  sessionStatus,
  editingIndex,
  editDraft,
  onClose,
  onMaximize,
  onChatMessageChange,
  onSend,
  onStop,
  onFeedback,
  onFileSelect,
  onFileRemove,
  onToggleSearch,
  onResume,
  onEditDraftChange,
  onStartEdit,
  onCancelEdit,
  onSubmitEdit,
  isFetchingSession,
}: IBuduGPTChatProps) => {
  const chatEndRef = useRef<HTMLDivElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [dislikedIndex, setDislikedIndex] = useState<number | null>(null);
  const [correction, setCorrection] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  const dragDepthRef = useRef(0);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chatHistory, isBusy]);

  const isDropDisabled =
    isBusy || isUploadingFile || !!uploadedFile || isFetchingSession;

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !validateChatFile(file)) return;
    onFileSelect(file);
  };

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    if (!isFileDrag(e)) return;
    e.preventDefault();
    if (isDropDisabled) return;
    dragDepthRef.current += 1;
    setIsDragging(true);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    if (!isFileDrag(e)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = isDropDisabled ? "none" : "copy";
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    if (!isFileDrag(e) || isDropDisabled) return;
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    if (!isFileDrag(e)) return;
    e.preventDefault();
    dragDepthRef.current = 0;
    setIsDragging(false);
    if (isDropDisabled) return;

    const files = Array.from(e.dataTransfer.files);
    if (files.length === 0) return;
    if (files.length > 1) {
      alert("Only one file can be attached at a time.");
      return;
    }
    if (!validateChatFile(files[0])) return;
    onFileSelect(files[0]);
  };

  const handleDislike = (index: number) => {
    onFeedback(index, -1);
    setDislikedIndex(index);
    setCorrection("");
  };

  const handleCorrectionSubmit = (index: number) => {
    const text = correction.trim();
    if (text) {
      onFeedback(index, -1, text);
    }
    setDislikedIndex(null);
    setCorrection("");
  };

  const handleCorrectionSkip = () => {
    setDislikedIndex(null);
    setCorrection("");
  };

  if (!isModalOpen) return null;

  const isAttachDisabled = isBusy || !!uploadedFile;
  const attachBtnStyle: React.CSSProperties = {
    height: "44px",
    width: "44px",
    borderRadius: "12px",
    border: "none",
    background: "#ebe8e0",
    fontSize: "18px",
    color: isUploadingFile ? "#7a6a3f" : isAttachDisabled ? "#bbb" : "#948256",
    cursor: isUploadingFile
      ? "progress"
      : isAttachDisabled
        ? "not-allowed"
        : "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    opacity: isAttachDisabled && !isUploadingFile ? 0.6 : 1,
  };

  return (
    <div
      className={`chat chatbot ${isFullScreen ? "chat_fullscreen" : ""}`}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {isDragging && !isDropDisabled && (
        <div
          style={{
            position: "absolute",
            inset: "8px",
            zIndex: 1000,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px",
            border: "2px dashed #948256",
            borderRadius: "12px",
            background: "rgba(250, 248, 244, 0.94)",
            pointerEvents: "none",
            userSelect: "none",
          }}
        >
          <i
            className="fas fa-cloud-upload-alt"
            style={{ color: "#948256", fontSize: "32px" }}
          />
          <span style={{ color: "#948256", fontSize: "14px", fontWeight: 600 }}>
            Drop file to attach
          </span>
          <span style={{ color: "#888", fontSize: "11px" }}>
            {ALLOWED_FILE_TYPES_LABEL}
          </span>
        </div>
      )}

      <div className="chatbot-header">
        <div className="img">
          <img src="/assets/images/chatbot.svg" alt="" />
          <h6>AI Assist</h6>
        </div>
        <div className="maximize">
          <button onClick={onMaximize} style={{ marginLeft: "10px" }}>
            <img src="/assets/images/icon/ico-maximise.svg" alt="" />
          </button>
          <button onClick={onClose} style={{ marginLeft: "10px" }}>
            <img src="/assets/images/icon/ico-chat-close.svg" alt="" />
          </button>
        </div>
      </div>

      {isFetchingSession ? (
        <div className="chat_body chat_login">
          <div className="intro d-flex align-items-center justify-content-center">
            <PulseLoader color="#948256" size={8} margin={3} />
          </div>
        </div>
      ) : (
        <>
          <div className="chat_body chat_login">
            <div className="intro">
              {chatHistory.map(
                (chat, index) =>
                  chat.content && (
                    <div key={index} style={{ marginBottom: "12px" }}>
                      {chat.role === AiChatRoles.USER && editingIndex === index ? (
                        <div
                          className="mb-2"
                          style={{ marginLeft: "auto", width: "100%", maxWidth: "80%", minWidth: "220px" }}
                        >
                        <textarea
                          autoFocus
                          rows={3}
                          value={editDraft}
                          onChange={(e) => onEditDraftChange(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" && !e.shiftKey) {
                              e.preventDefault();
                              onSubmitEdit(index);
                            }
                            if (e.key === "Escape") onCancelEdit();
                          }}
                          className="message-user edit-message"
                        />

                        <div className="d-flex justify-content-end gap-2 mt-2">
                          <button
                            onClick={onCancelEdit}
                            className="btn btn-outline-secondary btn-sm edit-message-btn"
                          >
                            Cancel
                          </button>

                          <button
                            onClick={() => onSubmitEdit(index)}
                            className="btn btn-primary btn-sm edit-message-btn"
                          >
                            Save
                          </button>
                        </div>
                      </div>
                      ) : (
                        <>
                          <div
                            className={`message-${chat.role} ${
                              chat.content === "Limit exceeded" ? "limit-exceeded" : ""
                            } mb-2`}
                          >
                            <MarkdownRenderer content={chat.content} />
                          </div>

                          {chat.role === AiChatRoles.USER && (
                            <div
                              className="text-end mb-1"
                              style={{ marginLeft: "auto", width: "fit-content" }}
                            >
                              <button
                                title="Edit"
                                onClick={() => onStartEdit(index)}
                                className="bg-transparent border-0 small p-0 text-secondary"
                                style={{ fontSize: "12px" }}
                              >
                                <i className="fas fa-pencil-alt" />
                              </button>
                            </div>
                          )}
                        </>
                      )}

                      {chat.role === AiChatRoles.ASSISTANT && (
                        <>
                          <div className="d-flex align-items-center gap-2 ms-5 mb-1">
                            <button
                              title="Like"
                              onClick={() => {
                                onFeedback(index, 1);
                                if (dislikedIndex === index) {
                                  setDislikedIndex(null);
                                  setCorrection("");
                                }
                              }}
                              className={`bg-transparent border-0 small p-0 ${
                                feedbackGiven[index] === 1
                                  ? "text-success"
                                  : "text-secondary"
                              }`}
                            >
                              <i className="fas fa-thumbs-up" />
                            </button>
                            <button
                              title="Dislike"
                              onClick={() => handleDislike(index)}
                              className={`bg-transparent border-0 small p-0 ${
                                feedbackGiven[index] === -1
                                  ? "text-danger"
                                  : "text-secondary"
                              }`}
                            >
                              <i className="fas fa-thumbs-down" />
                            </button>
                          </div>

                          {dislikedIndex === index && (
                            <div
                              className="ms-5 mb-2"
                              style={{ maxWidth: "320px" }}
                            >
                              <textarea
                                autoFocus
                                rows={2}
                                placeholder="What would a better response look like? (optional)"
                                value={correction}
                                onChange={(e) => setCorrection(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter" && !e.shiftKey) {
                                    e.preventDefault();
                                    handleCorrectionSubmit(index);
                                  }
                                  if (e.key === "Escape")
                                    handleCorrectionSkip();
                                }}
                                className="form-control form-control-sm mb-1"
                                style={{ resize: "none", fontSize: "12px" }}
                              />
                              <div className="d-flex gap-2">
                                <button
                                  onClick={() => handleCorrectionSubmit(index)}
                                  className="btn btn-sm btn-primary py-0 px-2"
                                  style={{ fontSize: "11px" }}
                                >
                                  Submit
                                </button>
                                <button
                                  onClick={handleCorrectionSkip}
                                  className="btn btn-sm btn-outline-secondary py-0 px-2"
                                  style={{ fontSize: "11px" }}
                                >
                                  Skip
                                </button>
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  ),
              )}

              {loader && (
                <div style={{ marginBottom: "12px" }}>
                  <div className="message-assistant p-3 rounded mb-2">
                    <PulseLoader color="#948256" size={8} margin={3} />
                  </div>
                </div>
              )}

              <div ref={chatEndRef} />
            </div>
          </div>

          {/* Resume banner — shown when a session was interrupted mid-stream */}
          {sessionStatus === "Pending" && onResume && (
            <div
              className="d-flex align-items-center justify-content-between px-3 py-1"
              style={{ borderTop: "1px solid #f0ece3", background: "#fffbf2" }}
            >
              <span
                className="small"
                style={{ fontSize: "12px", color: "#7a6030" }}
              >
                Response was interrupted.
              </span>
              <button
                onClick={onResume}
                className="btn btn-outline-secondary btn-sm edit-message-btn"
              >
                Resume
              </button>
            </div>
          )}
        </>
      )}

      {/* Upload error indicator */}
      {uploadError && !uploadedFile && (
        <div
          className="d-flex align-items-center gap-2 px-3 py-1"
          style={{ borderTop: "1px solid #f0ece3", background: "#fff5f5" }}
        >
          <i
            className="fas fa-exclamation-circle"
            style={{ color: "#c0392b", fontSize: "13px" }}
          />
          <span
            className="small flex-grow-1"
            style={{ fontSize: "12px", color: "#c0392b" }}
          >
            Unsupported file type. Please try again.
          </span>
          <button
            onClick={onFileRemove}
            className="bg-transparent border-0 p-0"
            title="Dismiss"
            style={{ color: "#c0392b", fontSize: "13px", lineHeight: 1 }}
          >
            <i className="fas fa-times" />
          </button>
        </div>
      )}

      {/* Attached file indicator — sits above the input row */}
      {uploadedFile && (
        <div
          className="d-flex align-items-center gap-3 px-3"
          style={{
            borderTop: "1px solid #f0ece3",
            background: "#faf8f4",
            paddingTop: "10px",
            paddingBottom: "10px",
          }}
        >
          <i
            className="fas fa-file-alt"
            style={{ color: "#948256", fontSize: "20px" }}
          />
          <span
            className="text-truncate flex-grow-1"
            title={uploadedFile.filename}
            style={{ fontSize: "15px", color: "#555" }}
          >
            {uploadedFile.filename}
          </span>
          {onToggleSearch && (
            <button
              onClick={onToggleSearch}
              className="bg-transparent border-0 p-0"
              title={
                searchGlobally ? "Global search: ON" : "Global search: OFF"
              }
              style={{ lineHeight: 1 }}
            >
              <WideToggle on={!!searchGlobally} />
            </button>
          )}
          <button
            onClick={onFileRemove}
            className="bg-transparent border-0 p-0"
            title="Remove file"
            style={{ color: "#c0392b", fontSize: "23px", lineHeight: 1 }}
          >
            <i className="fas fa-times" />
          </button>
        </div>
      )}

      <div className="fab_field chatfield">
        <input
          ref={fileInputRef}
          type="file"
          accept={ALLOWED_FILE_EXTENSIONS}
          style={{ display: "none" }}
          onChange={handleFileChange}
        />

        <textarea
          className="auto-resize-textarea"
          placeholder="Message Andy..."
          disabled={isBusy}
          value={chatMessage}
          onChange={(e) => onChatMessageChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              onSend();
            }
          }}
          style={isFullScreen ? undefined : { width: "calc(100% - 100px)" }}
        />

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "6px",
            flexShrink: 0,
          }}
        >
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={isBusy || isUploadingFile || !!uploadedFile}
            title={
              uploadedFile
                ? "Remove the current file to attach another"
                : "Attach file"
            }
            style={attachBtnStyle}
          >
            {isUploadingFile ? (
              <i className="fas fa-spinner fa-spin" />
            ) : (
              <i className="fas fa-paperclip" />
            )}
          </button>

          {isBusy || isStreaming ? (
            <button
              type="button"
              onClick={onStop}
              className="stop-btn"
              title="Stop generation"
            >
              <i className="fas fa-square" />
            </button>
          ) : (
            <button
              type="button"
              onClick={() => onSend()}
              disabled={!chatMessage.trim() || isBusy}
              title="Send message"
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default BuduGPTChat;
